import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import communityAPI from '../../../api/community';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import { InlineSpinner } from '../../../componentes/LoadingIndicator';
import GoogleLoginButton from '../../GoogleLoginButton';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshSession } = useCommunityAuth();
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    const trimmedIdentifier = formData.identifier.trim();
    if (!trimmedIdentifier) {
      setError('Username or email is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await communityAPI.auth.login({
        identifier: trimmedIdentifier,
        password: formData.password,
      });
      const nextUser = res.data?.user;
      if (nextUser) {
        login(nextUser);
        await refreshSession();
        const isAdminUser = Boolean(
          nextUser?.role === 'admin' ||
          nextUser?.is_staff ||
          nextUser?.is_superuser
        );
        navigate(isAdminUser ? '/admin/dashboard' : '/community/profile', {
          replace: true,
        });
        return;
      }
      navigate('/community/login', { replace: true });
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.error_type === 'email_not_verified') {
        navigate('/community/verify-otp', {
          state: {
            username: errorData.username,
            email: errorData.email,
            fromLogin: true
          }
        });
      } else {
        setError(errorData?.error || 'Invalid credentials');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = (user) => {
    login(user);
    refreshSession();
    const isAdminUser = Boolean(
      user?.role === 'admin' ||
      user?.is_staff ||
      user?.is_superuser
    );
    navigate(isAdminUser ? '/admin/dashboard' : '/community/profile', {
      replace: true,
    });
  };

  const handleGoogleError = (errorMsg) => {
    setError(errorMsg);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-8 shadow-2xl">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">Login</h2>
        
        {successMessage && (
          <div className="bg-green-100 dark:bg-green-900/30 border border-green-500 text-green-700 dark:text-green-400 rounded-xl p-3 mb-4">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-500 text-red-700 dark:text-red-400 rounded-xl p-3 mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">Username or email</label>
            <input
              type="text"
              value={formData.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl p-3 text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Sign in with either credential linked to your account.</p>
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl p-3 pr-12 text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary/80"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 bg-primary hover:bg-[#229bb8] rounded-xl text-white font-semibold shadow-lg transition-all transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <InlineSpinner
                tone="slate"
                message="Signing in..."
                messageClassName="text-white"
              />
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or continue with</span>
            </div>
          </div>
          <div className="mt-6">
            <GoogleLoginButton onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
          </div>
        </div>

        <div className="mt-4 text-center">
          <Link to="/community/forgot-password" className="text-primary dark:text-primary/80 hover:text-[#229bb8] dark:hover:text-primary/70 font-semibold text-sm">
            Forgot Password?
          </Link>
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Don't have an account?{' '}
            <Link to="/community/register" className="text-primary dark:text-primary/80 hover:text-[#229bb8] dark:hover:text-primary/70 font-semibold">
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
